import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { PageHeader } from '../components/common/PageHeader';
import { Reveal } from '../components/animation/Reveal';
import { Icon } from '../components/common/Icon';
import { CropArt } from '../components/common/CropArt';
import { cropColor } from '../lib/cropColor';
import { productRepository } from '../services/productRepository';
import { primaryImage } from '../lib/image';
import { accentAlpha } from '../lib/slug';
import { breadcrumbSchema, collectionPageSchema, itemListSchema } from '../lib/seo';
import { useCropSolutions } from '../hooks';
import type { CropSolution } from '../types';

// Curated groups, in the reference's fixed order.
const GROUPS = ['Fruits', 'Vegetables', 'Field Crops', 'Plantation & Spices'] as const;

function CropCard({ crop }: { crop: CropSolution }) {
  const products = crop.productSlugs
    .map((s) => productRepository.getProductBySlugSync(s))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));
  const accent = cropColor(crop.slug);
  const names = products.map((p) => p.name).join(', ');
  return (
    <Link
      to={`/crop-solutions/${crop.slug}`}
      className="group relative flex flex-col overflow-hidden rounded-xl border border-sand-200 bg-white p-6 shadow-soft transition-shadow duration-500 hover:shadow-lift"
    >
      <CropArt
        slug={crop.slug}
        style={{ color: accent }}
        className="pointer-events-none absolute -bottom-7 -right-5 h-40 w-40 opacity-[0.1] transition-all duration-500 ease-premium group-hover:-bottom-5 group-hover:opacity-[0.17]"
      />
      <div className="relative flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold text-ink">{crop.name}</h3>
        <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: accentAlpha(accent, 0.14), color: accent }}>
          <Icon name="Sprout" className="h-4 w-4" />
        </span>
      </div>
      {crop.intro && <p className="relative mt-2 text-sm leading-relaxed text-ink-500">{crop.intro}</p>}
      {names && (
        <p className="relative mt-3 flex-1 text-sm text-ink-400">
          <span className="font-medium text-ink-600">Programme:</span> {names}
        </p>
      )}
      <div className="relative mt-5 flex items-center justify-between">
        <div className="flex -space-x-2.5">
          {products.slice(0, 4).map((p) => {
            const img = primaryImage(p);
            return (
              <span key={p.id} className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-sand-200 bg-white">
                {img ? <img src={img} alt="" className="h-full w-full object-contain p-0.5" loading="lazy" /> : <Icon name="Droplets" className="h-4 w-4 text-ink-300" />}
              </span>
            );
          })}
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-600">
          View programme <Icon name="ArrowRight" className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
}

export default function CropSolutionsPage() {
  const { data: crops = [] } = useCropSolutions();

  return (
    <>
      <Seo
        title="Crop Solutions — GreenbayBio Programmes by Crop"
        description="GreenbayBio programmes for grape, pomegranate, sugarcane, citrus, cotton, banana, tomato, chilli, potato, onion, wheat and paddy, and turmeric and ginger — each with the products, crop stages and doses stated in the product specifications."
        path="/crop-solutions"
        keywords={['crop programme', 'bio fertilizer for paddy', 'biostimulant for grapes', 'crop wise fertilizer schedule']}
        jsonLd={[
          collectionPageSchema({
            name: 'GreenbayBio crop programmes',
            description:
              'Crop-by-crop programmes assembled from the application schedules in each product\u2019s technical specifications.',
            path: '/crop-solutions',
          }),
          itemListSchema(
            'Crop programmes',
            '/crop-solutions',
            crops.map((c) => ({ name: c.name, path: `/crop-solutions/${c.slug}` })),
          ),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Crop Solutions', path: '/crop-solutions' }]),
        ]}
      />
      <PageHeader
        eyebrow="Crop solutions"
        title="Products matched to your crop."
        subtitle="Every recommendation is built from verified, crop-by-crop application programmes — pick your crop to see the matched products."
      />

      <div className="container-x py-14">
        {crops.length === 0 ? (
          <p className="rounded-xl border border-dashed border-sand-200 bg-white px-6 py-16 text-center text-ink-500">
            Crop recommendations appear here once products are matched to the crops they suit.
          </p>
        ) : (
          <div className="space-y-14">
            {GROUPS.map((group) => {
              const inGroup = crops.filter((c) => c.group === group);
              if (!inGroup.length) return null;
              return (
                <section key={group}>
                  <h2 className="mb-6 font-display text-h2 font-semibold text-ink">{group}</h2>
                  <Reveal className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" stagger={0.08} y={24}>
                    {inGroup.map((crop) => (
                      <CropCard key={crop.slug} crop={crop} />
                    ))}
                  </Reveal>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
